import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "lucide-react";
import { API } from "@/context/AuthContext";

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await fetch(`${API}/auth/invitation/${token}`);
        if (!res.ok) {
          const err = await res.json();
          setError(err.detail || "Invalid invitation");
        } else {
          const data = await res.json();
          setInvitation(data);
        }
      } catch {
        setError("Failed to validate invitation");
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleAccept = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/council";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-normal text-[#0F172A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Invitation Invalid
          </h2>
          <p className="text-[#52525B] text-sm mb-6">{error}</p>
          <button onClick={() => navigate("/")} className="btn-secondary text-sm">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#064E3B] rounded-sm flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={28} className="text-white" />
          </div>
          <p className="section-label mb-3">You&apos;re invited</p>
          <h1 className="text-3xl font-normal text-[#0F172A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Welcome, {invitation?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#52525B] text-sm leading-relaxed">
            {invitation?.message || "You have been selected to join CouncilOne as a Council Member — our curated network of accomplished professionals."}
          </p>
        </div>

        <div className="card-flat mb-6">
          <h3 className="font-semibold text-[#0F172A] text-sm mb-4">What happens next?</h3>
          <div className="space-y-3">
            {[
              "Sign in with your Google account",
              "Complete your professional profile",
              "Set your availability and services",
              "Start receiving booking requests",
            ].map((step, i) => (
              <div key={step} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#0F172A] flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-white text-[10px] font-bold">{i + 1}</span>
                </div>
                <p className="text-sm text-[#52525B]">{step}</p>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleAccept}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
          data-testid="accept-invitation-btn"
        >
          Accept Invitation with Google
          <ArrowRight size={16} />
        </button>

        <p className="text-center text-[10px] text-[#A1A1AA] mt-4">
          By accepting, you agree to CouncilOne&apos;s Terms and Community Guidelines.
        </p>
      </div>
    </div>
  );
}
